import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { openExternalLink } from '../../utils/electron';

interface ShutdownScreenProps {
    isVisible: boolean;
    onComplete: () => void;
    isKernelCrash?: boolean;
    isRestart?: boolean;
}

export const ShutdownScreen: React.FC<ShutdownScreenProps> = ({ isVisible, onComplete, isKernelCrash = false, isRestart = false }) => {
    const { t } = useLanguage();
    const [shutdownLines, setShutdownLines] = useState<string[]>([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [isComplete, setIsComplete] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);

    // Récupérer les paramètres de jeu pour les sons
    const [gameSettings] = useState(() => {
        const saved = localStorage.getItem('ALBION_GAME_SETTINGS');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.warn('Error parsing game settings:', e);
            }
        }
        return {
            soundEnabled: true,
            soundVolume: 0.7
        };
    });

    // Son de bip système pour shutdown (identique au boot)
    const playShutdownSound = () => {
        if (!gameSettings.soundEnabled) return;

        try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            // Son de bip système plus authentique (comme un PC des années 90)
            osc.type = 'square';
            osc.frequency.setValueAtTime(800, ctx.currentTime);

            gain.gain.setValueAtTime(0.03 * gameSettings.soundVolume, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start();
            osc.stop(ctx.currentTime + 0.08);
        } catch (e) {
            // Ignore if audio context fails
        }
    };

    // Séquence d'extinction authentique années 90
    const getShutdownSequence = () => [
        t('shutdown.sequence.shuttingDown'),
        t('shutdown.sequence.closingApps'),
        t('shutdown.sequence.savingData'),
        t('shutdown.sequence.disconnectingNetwork'),
        t('shutdown.sequence.stoppingServices'),
        t('shutdown.sequence.flushingBuffers'),
        t('shutdown.sequence.syncingDisk'),
        t('shutdown.sequence.stoppingCpu'),
        t('shutdown.sequence.powerOff'),
        t('shutdown.sequence.systemStopped')
    ];

    // Fonction pour faire un fondu de sortie de la musique
    const fadeOutMusic = () => {
        // Émettre un événement pour que LoginScreen gère le fondu de la musique
        window.dispatchEvent(new CustomEvent('FADE_OUT_MUSIC'));
    };

    useEffect(() => {
        if (!isVisible) {
            setShutdownLines([]);
            setCurrentStep(0);
            setIsComplete(false);
            setFadeIn(false);
            return;
        }

        const shutdownSequence = getShutdownSequence();

        // Commencer le fondu d'entrée immédiatement
        setFadeIn(true);

        // Commencer le fondu de sortie de la musique
        fadeOutMusic();

        // Attendre 1 seconde pour le fondu d'entrée puis commencer la séquence
        setTimeout(() => {
            let lineIndex = 0;
            const interval = setInterval(() => {
                if (lineIndex < shutdownSequence.length) {
                    setShutdownLines(prev => [...prev, shutdownSequence[lineIndex]]);
                    setCurrentStep(lineIndex + 1);
                    
                    // Son seulement au début et à la fin (comme pour le boot)
                    if (lineIndex === 0 || lineIndex === shutdownSequence.length - 1) {
                        playShutdownSound();
                    }
                    
                    lineIndex++;
                } else {
                    clearInterval(interval);
                    // Attendre seulement 0.5 seconde après la dernière ligne puis faire l'effet d'extinction
                    setTimeout(() => {
                        setIsComplete(true);
                        
                        // Si c'est un kernel crash, rester sur écran noir et redémarrer
                        if (isKernelCrash) {
                            setTimeout(() => {
                                onComplete();
                            }, 3000); // 3 secondes d'écran noir (au lieu de 5)
                        } else if (isRestart) {
                            // Redémarrage - attendre 4 secondes après l'animation CRT puis recharger
                            setTimeout(() => {
                                console.log('ShutdownScreen - Redémarrage du système après 4 secondes');
                                onComplete();
                            }, 800 + 4000); // 800ms animation CRT + 4000ms d'attente
                        } else {
                            // Shutdown normal - ouvrir le lien à la fin de l'animation CRT puis fermer
                            setTimeout(() => {
                                // Ouvrir le lien communauté si demandé (juste avant de fermer)
                                const shouldOpenCommunityLink = localStorage.getItem('ALBION_OPEN_COMMUNITY_LINK');
                                if (shouldOpenCommunityLink === 'true') {
                                    console.log('ShutdownScreen - Opening community link in default browser at end of CRT animation');
                                    localStorage.removeItem('ALBION_OPEN_COMMUNITY_LINK');
                                    openExternalLink('https://www.facebook.com/profile.php?id=61586874591113');
                                }
                                
                                // Ouvrir le lien Itch.io si demandé (juste avant de fermer)
                                const shouldOpenItchLink = localStorage.getItem('ALBION_OPEN_ITCH_LINK');
                                if (shouldOpenItchLink === 'true') {
                                    console.log('ShutdownScreen - Opening Itch.io link in default browser at end of CRT animation');
                                    localStorage.removeItem('ALBION_OPEN_ITCH_LINK');
                                    openExternalLink('https://papycyclopede.itch.io/overwrite');
                                }
                                
                                // Fermer l'application
                                onComplete();
                            }, 800); // 800ms = durée de l'animation CRT
                        }
                    }, 500);
                }
            }, 200); // 200ms entre chaque ligne (au lieu de 400ms)

            return () => clearInterval(interval);
        }, 500); // Délai initial réduit à 500ms
    }, [isVisible, onComplete, t]);

    if (!isVisible) return null;

    const shutdownSequence = getShutdownSequence();

    return (
        <div className={`fixed inset-0 z-[10001] bg-black flex flex-col items-center justify-center font-mono text-cyan-400 transition-all duration-1000 ${fadeIn ? 'opacity-100' : 'opacity-0'
            }`}>
            {/* Effet de "tube cathodique qui s'éteint" - doit être au-dessus de tout */}
            {isComplete && (
                <div className="absolute inset-0 animate-crt-shutdown z-10"></div>
            )}

            {/* Lignes d'arrêt système - disparaissent rapidement quand l'extinction commence */}
            <div className={`w-full max-w-lg space-y-2 px-8 transition-opacity duration-300 relative z-0 text-center ${isComplete ? 'opacity-0' : 'opacity-100'
                } ${shutdownLines.length >= 7 ? 'screen-flicker' : ''}`}>
                {shutdownLines.map((line, i) => {
                    // Changement de couleur à partir de "SYNCHRONISATION DISQUE..." (index 6)
                    const isLowPowerMode = i >= 6;
                    const textColor = isLowPowerMode ? 'text-slate-300' : 'text-cyan-400';

                    // Effet de scintillement plus intense pour les 3 dernières lignes
                    const isDyingTerminal = i >= 7;

                    return (
                        <div
                            key={i}
                            className={`${textColor} uppercase text-sm transition-all duration-500 ${i === shutdownLines.length - 1 ? 'animate-pulse' : ''
                                } ${isDyingTerminal ? 'terminal-dying' : ''}`}
                            style={{
                                animationDelay: `${i * 100}ms`,
                                opacity: isComplete ? 0 : 1,
                                // Effet de scintillement de base pour les lignes en mode basse consommation
                                textShadow: isLowPowerMode && !isDyingTerminal ? '0 0 2px rgba(203, 213, 225, 0.5)' :
                                    !isLowPowerMode ? '0 0 4px rgba(34, 211, 238, 0.3)' : 'none'
                            }}
                        >
                            {line}
                        </div>
                    );
                })}

                {/* Curseur clignotant */}
                {!isComplete && (
                    <div className={`animate-pulse transition-colors duration-500 ${shutdownLines.length >= 6 ? 'text-slate-300' : 'text-cyan-400'
                        } ${shutdownLines.length >= 8 ? 'terminal-dying' : ''}`}>_</div>
                )}
            </div>

            {/* Barre de progression - disparaît aussi rapidement */}
            <div className={`mt-8 w-64 h-2 bg-slate-800 border border-slate-600 overflow-hidden transition-opacity duration-300 relative z-0 ${isComplete ? 'opacity-0' : 'opacity-100'
                }`}>
                <div
                    className="h-full bg-cyan-500 transition-all duration-300 ease-out"
                    style={{
                        width: `${(currentStep / shutdownSequence.length) * 100}%`
                    }}
                />
            </div>

            {/* Texte de progression - disparaît aussi rapidement */}
            <div className={`mt-4 text-xs text-slate-400 transition-opacity duration-300 relative z-0 ${isComplete ? 'opacity-0' : 'opacity-100'
                }`}>
                {t('shutdown.progress')} {Math.round((currentStep / shutdownSequence.length) * 100)}%
            </div>


            {/* Écran noir pour kernel crash et restart */}
            {isComplete && (isKernelCrash || isRestart) && (
                <div className="absolute inset-0 bg-black z-20 flex items-center justify-center">
                    {/* Écran complètement noir */}
                    {isRestart && (
                        <div className="text-cyan-400 text-sm uppercase animate-pulse">
                            {t('shutdown.restarting')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};